import { useCallback, useEffect, useMemo, useState } from 'react';
import usePersistentState from './usePersistentState';

function clamp(value, minimum, maximum) {
  return Math.min(Math.max(minimum, value), maximum);
}

function normalizeWindowSize(size, config) {
  const candidate = size ?? config.defaultSize;
  if (!candidate) return null;

  const minWidth = config.minSize?.width ?? 0;
  const minHeight = config.minSize?.height ?? 0;
  const maxWidth = config.maxSize?.width ?? Infinity;
  const maxHeight = config.maxSize?.height ?? Infinity;
  const width = Number.isFinite(Number(candidate.width)) ? Number(candidate.width) : config.defaultSize?.width;
  const height = Number.isFinite(Number(candidate.height)) ? Number(candidate.height) : config.defaultSize?.height;

  const sizeRangeWidth = maxWidth - minWidth;
  const sizeRangeHeight = maxHeight - minHeight;
  if (config.scaleSizeRange && Number.isFinite(sizeRangeWidth) && Number.isFinite(sizeRangeHeight)) {
    const widthProgress = sizeRangeWidth > 0 ? (width - minWidth) / sizeRangeWidth : 1;
    const heightProgress = sizeRangeHeight > 0 ? (height - minHeight) / sizeRangeHeight : 1;
    const nextProgress = clamp(Math.min(widthProgress, heightProgress), 0, 1);

    return {
      width: Math.round(minWidth + sizeRangeWidth * nextProgress),
      height: Math.round(minHeight + sizeRangeHeight * nextProgress),
    };
  }

  return {
    width: Math.round(clamp(width ?? minWidth, minWidth, maxWidth)),
    height: Math.round(clamp(height ?? minHeight, minHeight, maxHeight)),
  };
}

function normalizeWindows(windows, windowConfig, windowOrder) {
  const next = {};
  for (const key of windowOrder) {
    const config = windowConfig[key];
    const defaults = {
      open: config.defaultOpen ?? false,
      pinned: config.defaultPinned ?? false,
      compact: config.defaultCompact ?? config.defaultPinned ?? false,
      position: null,
      size: config.defaultSize ?? null,
    };
    next[key] = { ...defaults, ...(windows?.[key] || {}) };
    next[key].size = normalizeWindowSize(next[key].size, config);
  }
  return next;
}

export default function useWindowManager(windowConfig, windowOrder) {
  const [windows, setWindows] = usePersistentState('ui.windows', () => normalizeWindows(null, windowConfig, windowOrder));
  const [windowLayers, setWindowLayers] = useState(windowOrder);

  const windowState = useMemo(
    () => normalizeWindows(windows, windowConfig, windowOrder),
    [windows, windowConfig, windowOrder],
  );

  const focusWindow = useCallback((key) => {
    setWindowLayers(prev => [...prev.filter(item => item !== key), key]);
  }, []);

  const toggleWindow = useCallback((key) => {
    setWindows(prev => {
      const normalized = normalizeWindows(prev, windowConfig, windowOrder);
      const current = normalized[key];
      if (current.open) {
        normalized[key] = { ...current, open: false };
      } else {
        const mobile = typeof window !== 'undefined' && window.matchMedia?.('(max-width: 767px)')?.matches;
        if (mobile && !current.pinned) {
          for (const [otherKey, state] of Object.entries(normalized)) {
            if (otherKey !== key && state.open && !state.pinned) {
              normalized[otherKey] = { ...state, open: false };
            }
          }
        }
        normalized[key] = { ...current, open: true };
      }
      return { ...normalized };
    });
    focusWindow(key);
  }, [focusWindow, setWindows, windowConfig, windowOrder]);

  const togglePin = useCallback((key) => {
    setWindows(prev => {
      const normalized = normalizeWindows(prev, windowConfig, windowOrder);
      const pinned = !normalized[key].pinned;
      normalized[key] = { ...normalized[key], pinned, compact: pinned };
      return { ...normalized };
    });
    focusWindow(key);
  }, [focusWindow, setWindows, windowConfig, windowOrder]);

  const toggleCompact = useCallback((key) => {
    setWindows(prev => {
      const normalized = normalizeWindows(prev, windowConfig, windowOrder);
      normalized[key] = { ...normalized[key], compact: !normalized[key].compact };
      return { ...normalized };
    });
  }, [setWindows, windowConfig, windowOrder]);

  const moveWindow = useCallback((key, position) => {
    setWindows(prev => {
      const normalized = normalizeWindows(prev, windowConfig, windowOrder);
      normalized[key] = { ...normalized[key], position };
      return { ...normalized };
    });
  }, [setWindows, windowConfig, windowOrder]);

  const resizeWindow = useCallback((key, size) => {
    setWindows(prev => {
      const normalized = normalizeWindows(prev, windowConfig, windowOrder);
      normalized[key] = { ...normalized[key], size: normalizeWindowSize(size, windowConfig[key]) };
      return { ...normalized };
    });
  }, [setWindows, windowConfig, windowOrder]);

  useEffect(() => {
    const adaptMobileWindows = () => {
      if (!window.matchMedia?.('(max-width: 767px)')?.matches) return;
      setWindows(prev => {
        const normalized = normalizeWindows(prev, windowConfig, windowOrder);
        let changed = false;
        const frontmostUnpinned = [...windowLayers]
          .reverse()
          .find(key => normalized[key].open && !normalized[key].pinned);
        for (const key of windowOrder) {
          if (normalized[key].position) {
            normalized[key] = { ...normalized[key], position: null };
            changed = true;
          }
          if (key !== frontmostUnpinned && normalized[key].open && !normalized[key].pinned) {
            normalized[key] = { ...normalized[key], open: false };
            changed = true;
          }
        }
        return changed ? { ...normalized } : prev;
      });
    };
    adaptMobileWindows();
    window.addEventListener('resize', adaptMobileWindows);
    window.addEventListener('orientationchange', adaptMobileWindows);
    return () => {
      window.removeEventListener('resize', adaptMobileWindows);
      window.removeEventListener('orientationchange', adaptMobileWindows);
    };
  }, [setWindows, windowConfig, windowLayers, windowOrder]);

  return {
    focusWindow,
    moveWindow,
    resizeWindow,
    toggleCompact,
    togglePin,
    toggleWindow,
    windowLayers,
    windowState,
  };
}
